import { colors } from '@kmns-internal/furikake/tokens'
import { Stack, Typography } from '@mui/material'
import { format } from 'date-fns'
import { useHistory } from 'react-router-dom'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { demoPlaceNodes } from 'src/features/monitoringDashboardAlpha/demo/demoPlaceNodes'
import { useQueryDashboardReportDetails } from 'src/features/monitoringDashboardAlpha/detail/api/useQueryDashboardReportDetails'
import { DetailTable } from 'src/features/monitoringDashboardAlpha/detail/components/DetailTable/DetailTable'
import { ScheduleFilterDropDown } from 'src/features/monitoringDashboardAlpha/detail/components/ScheduleFilterDropDown/ScheduleFilterDropDown'
import { useDetailFilters } from 'src/features/monitoringDashboardAlpha/detail/hooks/useDetailFilters'
import { getDefaultDateRange } from 'src/features/monitoringDashboardAlpha/hooks/useMonitoringFilters'
import { ReportStatusGroupKey } from 'src/features/monitoringDashboardAlpha/types'

const statusLabel: Record<ReportStatusGroupKey, string> = {
  submitted: '実施済み',
  notImplemented: '期限切れ未実施',
  duringPeriod: '期間中',
  notStarted: '開始前',
}

/** 日付範囲の表示用文字列（指定なしは既定期間=今日。L1・データ取得と揃える） */
const formatDateRange = (dateRange: [string, string] | null): string => {
  const [from, through] = dateRange ?? getDefaultDateRange()
  const fmt = (iso: string) => format(new Date(iso), 'yyyy/MM/dd')
  return `${fmt(from)}-${fmt(through)}`
}

/**
 * モニタリングダッシュボード詳細（L2）
 *
 * L1 で選択したステータス・現場（と共通の日付範囲）で絞り込んだ
 * スケジュール×日付ごとの個別実施状況をフラットなリストで表示する。
 */
export const MonitoringDashboardDetail = () => {
  const history = useHistory()
  const { filters, setFilterOf } = useDetailFilters()
  const detailsQuery = useQueryDashboardReportDetails(filters)
  // セールスデモ（HRMSHI-250）の間は、見出しの現場名もダミーデータの現場から引く
  const placeNodes = demoPlaceNodes

  const {
    details = [],
    count = 0,
    scheduleOptions = [],
  } = detailsQuery.data || {}

  // ヘッダーに表示する現場名（複数なら件数表示）
  const placeLabel = (() => {
    if (filters.placeNodeUuids.length === 0) return 'すべて'
    const names = placeNodes
      .filter(node => filters.placeNodeUuids.includes(node.uuid))
      .map(node => node.name)
    if (names.length === 0) return `${filters.placeNodeUuids.length}件`
    return names.length <= 2 ? names.join('、') : `${names.length}件`
  })()

  return (
    <Stack sx={{ height: '100%' }}>
      <Header>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ minWidth: 0, overflow: 'hidden' }}
        >
          <HeaderBackButtonMui
            onClick={() => history.goBack()}
            sx={{ flexShrink: 0 }}
          />
          <Typography variant="h6" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            実施状況
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ minWidth: 0, overflow: 'hidden' }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{ color: colors.text.secondary, flexShrink: 0 }}
            >
              {formatDateRange(filters.dateRange)}
            </Typography>
            {/* 現場名が長い場合はヘッダーが崩れないよう省略表示する */}
            <Typography
              variant="body2"
              noWrap
              sx={{ color: colors.text.secondary, maxWidth: 280 }}
            >
              現場：{placeLabel}
            </Typography>
            <Typography
              variant="body2"
              noWrap
              sx={{ color: colors.text.secondary, flexShrink: 0 }}
            >
              ステータス：
              {filters.status ? statusLabel[filters.status] : 'すべて'}
            </Typography>
          </Stack>
        </Stack>
      </Header>

      <Stack
        spacing={2}
        sx={{ p: 3, bgcolor: colors.background.grayLight, flex: 1 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <ScheduleFilterDropDown
            options={scheduleOptions}
            value={filters.scheduleUuids}
            onChange={setFilterOf.scheduleUuids}
          />
        </Stack>

        {detailsQuery.isError ? (
          <ErrorMessage>エラーが発生しました</ErrorMessage>
        ) : (
          <DetailTable
            details={details}
            count={count}
            offset={filters.offset}
            limit={filters.limit}
            isLoading={detailsQuery.isPending || detailsQuery.isFetching}
            onChangePage={setFilterOf.offset}
            onChangeLimit={setFilterOf.limit}
          />
        )}
      </Stack>
    </Stack>
  )
}
